import type { Language } from '@sourcr/shared';

export const I18N = {
  en: {
    // Nav
    nav: {
      dashboard: 'Dashboard',
      newSearch: 'New Search',
      results: 'Results',
      favorites: 'Favorites',
      settings: 'Settings',
    },
    // Auth
    auth: {
      title: 'Sign in to Sourcr',
      subtitle: 'AI-powered candidate sourcing',
      continueWith: 'Continue with',
      google: 'Google',
      orEmail: 'Or sign in with email',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign In',
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      forgotPassword: 'Forgot password?',
      confirmPassword: 'Confirm Password',
      createAccount: 'Create Account',
      alreadyHaveAccount: 'Already have an account?',
      checkEmail: 'Check your email',
      checkEmailConfirm: 'We sent a confirmation link to',
      checkEmailReset: 'We sent a password reset link to',
      resendEmail: 'Resend',
      resetPassword: 'Reset Password',
      resetPasswordTitle: 'Reset your password',
      resetPasswordDesc: "Enter your email and we'll send you a reset link.",
      sendResetLink: 'Send reset link',
      newPassword: 'New Password',
      updatePassword: 'Update Password',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters',
      backToSignIn: 'Back to Sign In',
      googleNotEnabled: 'Google sign-in isn’t enabled yet. Please use email below to sign up or sign in.',
      heroTitle: 'Find the people, not just the keywords.',
      heroBody: 'Sourcr searches GitHub, LinkedIn, DOU, Djinni, Work.ua, Robota.ua and Telegram — then ranks every profile against your actual brief.',
      stat1: '7 sources · live',
      stat2: 'AI-ranked shortlists',
      stat3: 'Gap analysis on every match',
    },
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      totalSearches: 'Total Searches',
      candidatesFound: 'Candidates Found',
      favorites: 'Favorites',
      contacted: 'Contacted',
      recentSearches: 'Recent Searches',
      viewAll: 'View all',
      newSearch: 'New Search',
      deleteSearch: 'Delete',
      confirmDelete: 'Delete this search and all its candidates?',
      confirmYes: 'Yes, delete',
      confirmNo: 'Cancel',
      status: {
        pending: 'Pending',
        ready: 'Ready',
        running: 'Running',
        completed: 'Completed',
        failed: 'Failed',
      },
      noSearches: 'No searches yet. Start your first search!',
      activityFeed: 'Activity',
      filterStatus: 'Status',
      filterDate: 'Date',
      statusAll: 'All statuses',
      dateAll: 'All time',
      dateToday: 'Today',
      date7d: 'Last 7 days',
      date30d: 'Last 30 days',
      noFilterMatch: 'No searches match these filters.',
      welcomeSubtitle: 'Describe the role in plain language. The AI clarifies the brief, searches the sources you’re allowed to use, and returns a ranked shortlist with gap analysis.',
      onbDescribeTitle: 'Describe the role',
      onbDescribeDesc: 'Tell the AI who you’re hiring, in plain language.',
      onbSourcesTitle: 'Allowed sources',
      onbSourcesDesc: 'GitHub (API), public search across Work.ua / Robota.ua / LinkedIn with link-out, plus an opt-in candidate pool.',
      onbTrackTitle: 'Save & track',
      onbTrackDesc: 'Favorite candidates, add notes, track outreach status.',
    },
    // Chat
    chat: {
      title: 'New Search',
      placeholder: 'Describe the role you are sourcing for...',
      send: 'Send',
      criteria: 'Search Criteria',
      sources: 'Sources',
      startSearch: 'Start Search',
      searching: 'Searching...',
      runSearch: 'Run search',
      criteriaEmpty: 'Criteria will appear here as you chat',
      thinking: 'Thinking...',
      viewResults: 'View Results →',
      newSearch: 'Start New Search',
      searchFailed: 'Search failed — try again',
      searchCompleteToast: 'Search complete! View your candidates.',
      demo: 'Demo',
      assist: 'Link-out',
      srcLive: 'live',
      srcEmpty: 'no matches',
      srcUnavailable: 'unavailable',
      critRole: 'Role',
      critSkills: 'Skills',
      critLocation: 'Location',
      critExperience: 'Experience',
      critRemote: 'Remote',
      critInclude: 'Looking at',
      critExclude: 'Not looking at',
      critYes: 'Yes',
      critNo: 'No',
      critYears: 'years',
    },
    // Results
    results: {
      title: 'Candidates',
      found: 'found',
      filter: 'Filter',
      sortBy: 'Sort by',
      score: 'Score',
      addToFavorites: 'Add to favorites',
      removeFromFavorites: 'Remove from favorites',
      viewProfile: 'View Profile',
      skills: 'Skills',
      experience: 'Experience',
      gaps: 'Gaps',
      noResults: 'No candidates found. Try adjusting your search criteria.',
      loading: 'Searching across sources...',
      backToList: '← Back',
      demo: 'Demo',
      assist: 'Link-out',
      assistNote: 'Found via public search — open the profile to review before importing.',
      noFilterMatch: 'No candidates match the current filters.',
      loadMore: 'Load more',
      loadingMore: 'Loading…',
      exportCSV: 'Export CSV',
      allGrades: 'All grades',
      gradeA: 'A (80+)',
      gradeB: 'B (60+)',
      gradeC: 'C (40+)',
      allSources: 'All sources',
      filterSkillsPlaceholder: 'Filter skills...',
      filterByGrade: 'Filter by grade',
      filterBySource: 'Filter by source',
      filterBySkill: 'Filter by skill',
      sourcesLabel: 'Sources',
      srcLive: 'live',
      srcEmpty: 'no matches',
      srcUnavailable: 'unavailable',
      srcSearching: 'searching',
      srcDemo: 'demo',
      assistKeyHint: 'Public-profile search is unavailable. Set a SEARCH_API_KEY (Serper/Brave) to enable reliable X-ray results.',
      criteriaTitle: 'Criteria',
      critStatusMatch: 'confirmed',
      critStatusMismatch: "doesn't match",
      critStatusUnknown: 'no data',
      critNoData: 'no data',
      scoreUnavailable: 'Score unavailable',
      scoreUnavailableHint: 'Ranking did not complete for this candidate.',
      searchFilters: 'Search filters',
      filterInclude: 'Include',
      filterExclude: 'Exclude',
    },
    // Favorites
    favorites: {
      title: 'Favorites',
      noFavorites: 'No favorites yet. Add candidates from search results.',
      removeFromFavorites: 'Remove from favorites',
      viewProfile: 'View profile',
      startSearch: 'Start a search',
      countLabel: 'candidates',
      status: {
        pending: 'Pending',
        reviewed: 'Reviewed',
        contacted: 'Contacted',
        declined: 'Declined',
      },
      notes: 'Notes',
      notesPlaceholder: 'Add notes about this candidate...',
      save: 'Save',
    },
    // Settings
    settings: {
      title: 'Settings',
      sources: 'Data Sources',
      sourcesDescription: 'Enable or disable sourcing from each platform',
      kindLiveOfficial: 'Live (official)',
      kindAssist: 'Link-out',
      kindLiveOwn: 'Live (own pool)',
      kindPublicBeta: 'Public/Beta',
      kindDemo: 'Demo',
      appearance: 'Appearance',
      language: 'Language',
      theme: 'Theme',
      dark: 'Dark',
      light: 'Light',
      accent: 'Accent Color',
      density: 'Density',
      compact: 'Compact',
      regular: 'Regular',
      comfy: 'Comfy',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'Something went wrong',
      retry: 'Retry',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      search: 'Search',
      noData: 'No data available',
    },
  },
  uk: {
    nav: {
      dashboard: 'Панель',
      newSearch: 'Новий пошук',
      results: 'Результати',
      favorites: 'Обрані',
      settings: 'Налаштування',
    },
    auth: {
      title: 'Увійти в Sourcr',
      subtitle: 'AI-пошук кандидатів',
      continueWith: 'Продовжити через',
      google: 'Google',
      orEmail: 'Або увійдіть з email',
      email: 'Email',
      password: 'Пароль',
      signIn: 'Увійти',
      noAccount: 'Немає облікового запису?',
      signUp: 'Зареєструватись',
      forgotPassword: 'Забули пароль?',
      confirmPassword: 'Підтвердьте пароль',
      createAccount: 'Створити акаунт',
      alreadyHaveAccount: 'Вже є акаунт?',
      checkEmail: 'Перевірте пошту',
      checkEmailConfirm: 'Ми надіслали посилання на',
      checkEmailReset: 'Ми надіслали посилання для скидання на',
      resendEmail: 'Надіслати знову',
      resetPassword: 'Скинути пароль',
      resetPasswordTitle: 'Скидання паролю',
      resetPasswordDesc: 'Введіть email і ми надішлемо посилання.',
      sendResetLink: 'Надіслати посилання',
      newPassword: 'Новий пароль',
      updatePassword: 'Оновити пароль',
      passwordMismatch: 'Паролі не збігаються',
      passwordTooShort: 'Мінімум 8 символів',
      backToSignIn: 'Повернутись до входу',
      googleNotEnabled: 'Вхід через Google ще не увімкнено. Скористайтесь email нижче, щоб зареєструватись або увійти.',
      heroTitle: 'Знаходимо людей, а не просто ключові слова.',
      heroBody: 'Sourcr шукає в GitHub, LinkedIn, DOU, Djinni, Work.ua, Robota.ua та Telegram — і ранжує кожен профіль під ваш конкретний бриф.',
      stat1: '7 джерел · онлайн',
      stat2: 'AI-ранжовані шортлісти',
      stat3: 'Gap-аналіз кожного збігу',
    },
    dashboard: {
      title: 'Панель',
      totalSearches: 'Пошуків',
      candidatesFound: 'Знайдено кандидатів',
      favorites: 'Обраних',
      contacted: 'Контактовано',
      recentSearches: 'Останні пошуки',
      viewAll: 'Всі',
      newSearch: 'Новий пошук',
      deleteSearch: 'Видалити',
      confirmDelete: 'Видалити цей пошук і всіх знайдених кандидатів?',
      confirmYes: 'Так, видалити',
      confirmNo: 'Скасувати',
      status: {
        pending: 'Очікує',
        ready: 'Готово',
        running: 'Виконується',
        completed: 'Завершено',
        failed: 'Помилка',
      },
      noSearches: 'Ще немає пошуків. Розпочніть перший!',
      activityFeed: 'Активність',
      filterStatus: 'Статус',
      filterDate: 'Дата',
      statusAll: 'Усі статуси',
      dateAll: 'Весь час',
      dateToday: 'Сьогодні',
      date7d: 'Останні 7 днів',
      date30d: 'Останні 30 днів',
      noFilterMatch: 'Немає пошуків за цими фільтрами.',
      welcomeSubtitle: 'Опишіть вакансію звичайною мовою. ШІ уточнить деталі, знайде по дозволених джерелах і поверне ранжований шортліст з gap-аналізом.',
      onbDescribeTitle: 'Опишіть роль',
      onbDescribeDesc: 'Розкажіть ШІ, кого шукаєте, звичайною мовою.',
      onbSourcesTitle: 'Дозволені джерела',
      onbSourcesDesc: 'GitHub (API), публічний пошук по Work.ua / Robota.ua / LinkedIn з переходом на джерело, плюс opt-in пул кандидатів.',
      onbTrackTitle: 'Зберігай і відстежуй',
      onbTrackDesc: 'Обрані кандидати, нотатки, статус аутрічу.',
    },
    chat: {
      title: 'Новий пошук',
      placeholder: 'Опишіть роль, для якої ви шукаєте кандидатів...',
      send: 'Надіслати',
      criteria: 'Критерії пошуку',
      sources: 'Джерела',
      startSearch: 'Почати пошук',
      searching: 'Пошук...',
      runSearch: 'Запустити пошук',
      criteriaEmpty: 'Критерії зʼявляться тут у процесі розмови',
      thinking: 'Обробка...',
      viewResults: 'Переглянути результати →',
      newSearch: 'Новий пошук',
      searchFailed: 'Пошук не вдався — спробуйте ще раз',
      searchCompleteToast: 'Пошук завершено! Переглянте кандидатів.',
      demo: 'ДЕМО',
      assist: 'Link-out',
      srcLive: 'наживо',
      srcEmpty: 'немає збігів',
      srcUnavailable: 'недоступно',
      critRole: 'Посада',
      critSkills: 'Навички',
      critLocation: 'Локація',
      critExperience: 'Досвід',
      critRemote: 'Віддалено',
      critInclude: 'Розглядаємо',
      critExclude: 'Не розглядаємо',
      critYes: 'Так',
      critNo: 'Ні',
      critYears: 'років',
    },
    results: {
      title: 'Кандидати',
      found: 'знайдено',
      filter: 'Фільтр',
      sortBy: 'Сортування',
      score: 'Оцінка',
      addToFavorites: 'Додати до обраних',
      removeFromFavorites: 'Видалити з обраних',
      viewProfile: 'Переглянути профіль',
      skills: 'Навички',
      experience: 'Досвід',
      gaps: 'Прогалини',
      noResults: 'Кандидатів не знайдено. Спробуйте змінити критерії.',
      loading: 'Пошук у джерелах...',
      backToList: '← Назад',
      demo: 'ДЕМО',
      assist: 'Link-out',
      assistNote: 'Знайдено через публічний пошук — відкрийте профіль для перевірки перед імпортом.',
      noFilterMatch: 'Жоден кандидат не відповідає поточним фільтрам.',
      loadMore: 'Завантажити ще',
      loadingMore: 'Завантаження…',
      exportCSV: 'Експорт CSV',
      allGrades: 'Усі оцінки',
      gradeA: 'A (80+)',
      gradeB: 'B (60+)',
      gradeC: 'C (40+)',
      allSources: 'Усі джерела',
      filterSkillsPlaceholder: 'Фільтр за навичками...',
      filterByGrade: 'Фільтр за оцінкою',
      filterBySource: 'Фільтр за джерелом',
      filterBySkill: 'Фільтр за навичкою',
      sourcesLabel: 'Джерела',
      srcLive: 'наживо',
      srcEmpty: 'немає збігів',
      srcUnavailable: 'недоступно',
      srcSearching: 'пошук',
      srcDemo: 'демо',
      assistKeyHint: 'Пошук публічних профілів недоступний. Задайте SEARCH_API_KEY (Serper/Brave) для надійних X-ray результатів.',
      criteriaTitle: 'Критерії',
      critStatusMatch: 'підтверджено',
      critStatusMismatch: 'не відповідає',
      critStatusUnknown: 'немає даних',
      critNoData: 'немає даних',
      scoreUnavailable: 'Оцінка недоступна',
      scoreUnavailableHint: 'Ранжування не завершилось для цього кандидата.',
      searchFilters: 'Фільтри пошуку',
      filterInclude: 'Враховувати',
      filterExclude: 'Виключити',
    },
    favorites: {
      title: 'Обрані',
      noFavorites: 'Ще немає обраних. Додайте кандидатів з результатів пошуку.',
      removeFromFavorites: 'Видалити з обраних',
      viewProfile: 'Переглянути профіль',
      startSearch: 'Почати пошук',
      countLabel: 'кандидатів',
      status: {
        pending: 'Очікує',
        reviewed: 'Переглянуто',
        contacted: 'Контактовано',
        declined: 'Відхилено',
      },
      notes: 'Нотатки',
      notesPlaceholder: 'Додайте нотатки про кандидата...',
      save: 'Зберегти',
    },
    settings: {
      title: 'Налаштування',
      sources: 'Джерела даних',
      sourcesDescription: 'Увімкніть або вимкніть пошук на кожній платформі',
      kindLiveOfficial: 'Live (офіційне)',
      kindAssist: 'Link-out',
      kindLiveOwn: 'Live (власний пул)',
      kindPublicBeta: 'Public/Beta',
      kindDemo: 'Демо',
      appearance: 'Вигляд',
      language: 'Мова',
      theme: 'Тема',
      dark: 'Темна',
      light: 'Світла',
      accent: 'Акцентний колір',
      density: 'Щільність',
      compact: 'Компактна',
      regular: 'Звичайна',
      comfy: 'Комфортна',
    },
    common: {
      loading: 'Завантаження...',
      error: 'Щось пішло не так',
      retry: 'Повторити',
      cancel: 'Скасувати',
      save: 'Зберегти',
      delete: 'Видалити',
      close: 'Закрити',
      back: 'Назад',
      next: 'Далі',
      search: 'Пошук',
      noData: 'Дані відсутні',
    },
  },
} as const satisfies Record<Language, unknown>;

export type I18nKey = keyof typeof I18N.en;

export function t(lang: Language, section: keyof typeof I18N.en, key: string): string {
  const s = I18N[lang][section] as Record<string, unknown>;
  const val = s[key];
  return typeof val === 'string' ? val : key;
}
